import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageForm from '../../templates/PageForm';
import useFetch from '../../../hooks/useFetch';
import FormSkeleton from '../../atoms/FormSkeleton';
import { IoMdArrowRoundBack } from 'react-icons/io';
import { FiInfo } from 'react-icons/fi';
import RuleDetailModal from './RuleDetailModal';
import appConfig from '../../../config/app-config';

const API_KEY = appConfig.apis.softwarePortfolioApiEnv.key;
const BASE_URL = `${appConfig.apis.softwarePortfolioApiEnv.address}/softwares/analysis`;

const gradeColor: Record<string, string> = {
    A: 'bg-green-500', B: 'bg-blue-500', C: 'bg-yellow-500', D: 'bg-orange-500', E: 'bg-red-500',
};

const severityColor: Record<string, string> = {
    critical: 'bg-red-100 text-red-800 border-red-300',
    warning: 'bg-yellow-100 text-yellow-800 border-yellow-300',
};

const pillarLabels: Record<string, string> = {
    architecture: 'Arquitetura', complexity: 'Complexidade', tests: 'Testes',
    operability: 'Operabilidade', security: 'Segurança',
};

const PillarBar = ({ label, score, max }: { label: string; score: number; max: number }) => {
    const pct = max > 0 ? (score / max) * 100 : 0;
    const color = pct >= 80 ? 'bg-green-500' : pct >= 60 ? 'bg-yellow-500' : pct >= 40 ? 'bg-orange-500' : 'bg-red-500';
    return (
        <div className="space-y-1">
            <div className="flex justify-between text-sm">
                <span className="text-gray-700 font-medium">{label}</span>
                <span className="font-bold">{score}/{max}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div className={`${color} h-2.5 rounded-full`} style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
};

const SoftwareAnalysisDetailPageView: React.FC = () => {
    const { software = '' } = useParams();
    const navigate = useNavigate();
    const [selectedRule, setSelectedRule] = useState<string | null>(null);
    const { isLoading, data, error, isTokenError } = useFetch(
        `${BASE_URL}/${software}`,
        [
            { key: 'x-api-key', value: API_KEY ?? '' },
            { key: 'Authorization', value: `Bearer ${localStorage.getItem('authToken')}` },
        ]
    );

    if (isTokenError) navigate('/denied');
    if (error) navigate('/network-error');

    const score = data?.score;
    const pillars = score?.pillars || {};
    const compliance = data?.compliance;
    const structure = data?.structure;
    const violations = data?.violations || [];

    return (
        <PageForm
            pageTitle={`Análise: ${software}`}
            apiVersion={data ? `${data.apiName} - ${data.apiVersion}` : ''}
            isLoading={isLoading}
            breadcrumb={[
                { label: 'TI', url: '#' },
                { label: 'Análise de Engenharia', url: '/ti/software-analysis' },
                { label: software, url: '#' },
            ]}
            controlSlot={
                <div className="flex justify-end">
                    <button className="text-black bg-gray-300 hover:bg-gray-400 px-8 py-2.5 rounded" onClick={() => navigate('/ti/software-analysis')}>
                        <IoMdArrowRoundBack />
                    </button>
                </div>
            }
        >
            <div className="p-4 space-y-6">
                {isLoading ? <FormSkeleton /> : data && (
                    <>
                        {/* Header com score */}
                        <div className="grid grid-cols-4 gap-4">
                            <div className="bg-white border rounded-lg p-4 text-center shadow-sm col-span-1">
                                <p className="text-sm text-gray-500">Score Final</p>
                                <p className="text-4xl font-bold">{score?.final}</p>
                                <span className={`${gradeColor[score?.grade]} text-white text-sm font-bold px-3 py-1 rounded mt-2 inline-block`}>{score?.grade}</span>
                            </div>
                            <div className="bg-white border rounded-lg p-4 shadow-sm col-span-1">
                                <p className="text-sm text-gray-500">Estrutura</p>
                                <p className="text-lg font-bold">{data.structure_type}</p>
                                <p className="text-sm text-gray-400 mt-1">{structure?.module_count} módulos</p>
                                <p className="text-xs text-gray-400">Camadas: {structure?.layers?.join(', ')}</p>
                            </div>
                            <div className="bg-white border rounded-lg p-4 shadow-sm col-span-1">
                                <p className="text-sm text-gray-500">Arquivos por Camada</p>
                                {structure?.files_per_layer && Object.entries(structure.files_per_layer as Record<string, number>).map(([layer, count]) => (
                                    <div key={layer} className="flex justify-between text-sm mt-1">
                                        <span className="text-gray-600">{layer}</span>
                                        <span className="font-mono font-bold">{count}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="bg-white border rounded-lg p-4 shadow-sm col-span-1">
                                <p className="text-sm text-gray-500">Compliance</p>
                                <p className="text-2xl font-bold">{compliance?.total_cost?.toFixed(1)}</p>
                                <p className={`text-sm font-semibold ${compliance?.cost_band === 'critico' ? 'text-red-600' : compliance?.cost_band === 'alto' ? 'text-orange-600' : 'text-yellow-600'}`}>
                                    {compliance?.cost_band}
                                </p>
                            </div>
                        </div>

                        {/* Pilares */}
                        <div className="bg-white border rounded-lg p-4 shadow-sm">
                            <h3 className="text-sm font-semibold text-gray-700 mb-4">Pilares de Qualidade</h3>
                            <div className="space-y-3">
                                {Object.entries(pillars).map(([key, val]: [string, any]) => (
                                    <PillarBar key={key} label={pillarLabels[key] || key} score={val.score} max={val.max} />
                                ))}
                            </div>
                        </div>

                        {/* Compliance por regra */}
                        {compliance?.by_rule?.length > 0 && (
                            <div className="bg-white border rounded-lg shadow-sm">
                                <h3 className="text-sm font-semibold text-gray-700 p-4 border-b">Custo de Compliance por Regra</h3>
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                                        <tr>
                                            <th className="px-4 py-2 text-left">Regra</th>
                                            <th className="px-4 py-2 text-center">Ocorrências</th>
                                            <th className="px-4 py-2 text-center">Arquivos</th>
                                            <th className="px-4 py-2 text-center">Severidade</th>
                                            <th className="px-4 py-2 text-center">Spread</th>
                                            <th className="px-4 py-2 text-right">Custo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {compliance.by_rule.map((r: any) => (
                                            <tr key={r.rule_id} className="border-b hover:bg-slate-50">
                                                <td className="px-4 py-2 font-mono font-medium">
                                                    {r.rule_id}
                                                    <FiInfo className="inline ml-1 text-blue-400 hover:text-blue-600 cursor-pointer" onClick={() => setSelectedRule(r.rule_id)} />
                                                </td>
                                                <td className="px-4 py-2 text-center">{r.occurrences}</td>
                                                <td className="px-4 py-2 text-center">{r.files?.length}</td>
                                                <td className="px-4 py-2 text-center">{r.severity}</td>
                                                <td className="px-4 py-2 text-center">{r.spread}</td>
                                                <td className="px-4 py-2 text-right font-bold text-orange-600">{r.cost?.toFixed(1)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Violations */}
                        {violations.length > 0 && (
                            <div className="bg-white border rounded-lg shadow-sm">
                                <h3 className="text-sm font-semibold text-gray-700 p-4 border-b">
                                    Violações ({violations.length})
                                </h3>
                                <div className="max-h-96 overflow-y-auto divide-y">
                                    {violations.map((v: any, i: number) => (
                                        <div key={i} className="px-4 py-2 flex items-center gap-3 text-sm">
                                            <span className={`border text-xs font-bold px-2 py-0.5 rounded ${severityColor[v.severity] || 'bg-gray-100'}`}>
                                                {v.severity}
                                            </span>
                                            <span className="font-mono text-gray-600 w-24 shrink-0">
                                                {v.rule_id}
                                                <FiInfo className="inline ml-1 text-blue-400 hover:text-blue-600 cursor-pointer" onClick={() => setSelectedRule(v.rule_id)} />
                                            </span>
                                            <span className="text-gray-500 truncate flex-1" title={v.file}>{v.file}</span>
                                            <span className="text-gray-400 text-xs shrink-0">{v.message}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <p className="text-xs text-gray-400 text-right">Escaneado em: {new Date(data.scan_timestamp).toLocaleString('pt-BR')}</p>
                    </>
                )}
            </div>
            <RuleDetailModal ruleId={selectedRule} show={!!selectedRule} onClose={() => setSelectedRule(null)} />
        </PageForm>
    );
};

export default SoftwareAnalysisDetailPageView;
